import { useEffect, type ReactNode } from 'react';

import { useLanguageStore } from '@/modules/core';

interface DirectionProviderProps {
  children: ReactNode;
}

/**
 * DirectionProvider manages the document-level text direction and language attributes
 * based on the current language selection from the language store.
 *
 * This component listens to language changes and automatically updates:
 * - `dir` attribute on <html> element (ltr/rtl)
 * - `lang` attribute on <html> element (en/ar)
 *
 * This ensures RTL layout is applied globally when Arabic is selected,
 * and accessibility tools can properly identify the document language.
 *
 * @example
 * ```tsx
 * <DirectionProvider>
 *   <App />
 * </DirectionProvider>
 * ```
 */
export function DirectionProvider({ children }: DirectionProviderProps) {
  const { language, isRTL } = useLanguageStore();

  useEffect(() => {
    const html = document.documentElement;

    // Update direction attribute
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');

    // Update language attribute for accessibility
    html.setAttribute('lang', language);
  }, [language, isRTL]);

  return children;
}
